import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { RestrictedCustomer } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { complaintRatePercent } from './complaintRatePercent'
import RestrictedBadge from './RestrictedBadge'
import RowActionsMenu from './RowActionsMenu'

const t = buildTranslate('recovery_ai.restricted_customers')

export const SKELETON_ROW_COUNT = 5
// Keep in sync with the <th> count below — 5 data columns + 1 actions column.
// Skeleton rows and the empty-state colSpan both use this.
const COLUMN_COUNT = 6

type Props = {
  rows: RestrictedCustomer[]
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  // Consumed by the per-row overflow menu (U4); the pane owns the confirm + mutation.
  canManage: boolean
  onRemove: (row: RestrictedCustomer) => void
}

// Presentational only — no data fetching, no dispatch, no ability hook. The pane
// (RestrictedCustomers/index.tsx) owns the query, the CASL gate, and the confirm
// flow; this table copies the Activity/ActivityTable template (header cells,
// skeleton, empty, error/retry) for the restricted list's 5 columns.
const RestrictedCustomersTable = ({
  rows,
  isLoading = false,
  isError = false,
  onRetry,
  canManage,
  onRemove,
}: Props) => {
  const [css] = useStyletron()

  const headerCellClass = css({
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    textAlign: 'left',
  })

  const headerCellClassRight = css({
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    textAlign: 'right',
  })

  const cellClass = css({
    padding: '16px',
    fontSize: '14px',
    color: merchantTheme.colors.grey70,
    borderBottom: `1px solid ${merchantTheme.colors.grey30}`,
  })

  const cellClassRight = css({
    padding: '16px',
    fontSize: '14px',
    color: merchantTheme.colors.grey70,
    borderBottom: `1px solid ${merchantTheme.colors.grey30}`,
    textAlign: 'right',
  })

  if (isError) {
    return (
      <div className={css({ padding: '48px 0', textAlign: 'center' })}>
        <Text.BodyText3 color="grey70" className="mb-s">
          {t('error.body')}
        </Text.BodyText3>
        {onRetry && (
          <Button kind="secondary" onClick={onRetry}>
            {t('error.retry')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <table className={css({ width: '100%', borderCollapse: 'collapse' })}>
      <caption className="sr-only">{t('table.caption')}</caption>
      <thead>
        <tr>
          <th scope="col" className={headerCellClass}>
            {t('table.customer')}
          </th>
          <th scope="col" className={headerCellClassRight}>
            {t('table.complaints_30d')}
          </th>
          <th scope="col" className={headerCellClassRight}>
            {t('table.visits_30d')}
          </th>
          <th scope="col" className={headerCellClassRight}>
            {t('table.complaint_rate')}
          </th>
          <th scope="col" className={headerCellClass}>
            {t('table.status')}
          </th>
          <th scope="col" className={headerCellClass}>
            <span className="sr-only">{t('table.actions')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
            <tr
              key={`skeleton-row-${i}`}
              data-testid="restricted-skeleton-row"
              aria-hidden="true"
            >
              {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
                <td key={`skeleton-cell-${i}-${j}`} className={cellClass}>
                  <div
                    className={css({
                      height: '14px',
                      borderRadius: '4px',
                      backgroundColor: merchantTheme.colors.grey20,
                    })}
                  />
                </td>
              ))}
            </tr>
          ))
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={COLUMN_COUNT}>
              <div className={css({ padding: '48px 0', textAlign: 'center' })}>
                <Text.BodyText3 color="grey50">{t('empty')}</Text.BodyText3>
              </div>
            </td>
          </tr>
        ) : (
          rows.map(row => (
            <tr key={row.id}>
              <td className={cellClass}>{row.customerName}</td>
              <td className={cellClassRight}>{row.complaints30d}</td>
              <td className={cellClassRight}>{row.visits30d}</td>
              <td className={cellClassRight}>
                {complaintRatePercent(row.complaintRate)}
              </td>
              <td className={cellClass}>
                <RestrictedBadge status={row.status} />
              </td>
              <td className={cellClass}>
                <RowActionsMenu
                  row={row}
                  canManage={canManage}
                  onRemove={onRemove}
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

export default RestrictedCustomersTable
